import { useCallback } from 'react';
import { usePimlico } from '@package/smart-wallet/hooks';
import { TTransaction } from '@package/smart-wallet/interfaces';
import { Call, Hex } from 'viem';

import {
  IBuildTransaction,
  IExecuteTransaction,
  IExecuteTransactionProps,
  IGetPaymaster,
  IGetTransactionProvider,
} from './useExecuteTransaction.interface';

/**
 * Transaction object structure
 * {
 *  to: address,
 *  value: value,
 *  data: hex,
 * }
 */

/**
 * @param {boolean} isSponsor - If the transaction is a sponsor transaction
 * @param {object} {onSuccess, onSubmit, onError} handlers - Handlers for the transaction
 */
export const useExecuteTransactions = ({
  isSponsor = true,
  handlers,
}: IExecuteTransactionProps = {}) => {
  const { smartAccountClient } = usePimlico();

  const handleError = useCallback(
    (error: string) => {
      console.error('=== [TransactionError]', error);
      if (handlers?.onError) handlers.onError(error);
    },
    [handlers],
  );

  /*
   * @notice Handle the success of the transaction
   * @param {string} hash - Transaction hash
   * @param {object} opHash - userOperation hash
   * */
  const handleSuccess = useCallback(
    (txHash: string) => {
      console.log('=== [TransactionSuccess]', txHash);
      if (handlers?.onSuccess) handlers.onSuccess(txHash);
    },
    [handlers],
  );

  /*
   * @notice Handle the submit of the transaction
   * @param {object} opHash - User operation
   * */
  const handleSubmit = useCallback(
    (calls: Call[]) => {
      console.log('=== [TransactionSubmit]', calls);
      if (handlers?.onSubmit) handlers.onSubmit(calls);
    },
    [handlers],
  );

  /*
   *  @notice Get the paymaster for the transaction
   *  @param {object} {gasToken} - erc20 to charge gas fee
   */

  const getPaymaster = useCallback(({ gasToken }: IGetPaymaster) => {
    // right now is undefined because we only use sponsor txs
    console.log('=== [GetPaymaster]', gasToken);
    return undefined;
  }, []);

  /*
   * @param {object} {transactions, gasToken} - Transaction object
   * @param {array} transactions - Array of transaction objects
   * @param {object} gasToken - token to charge gas fee
   * */
  const buildTransactions = useCallback(
    ({ transactions, gasToken }: IBuildTransaction) => {
      const paymaster = getPaymaster({ gasToken: gasToken });

      if (!Array.isArray(transactions))
        throw new Error('Transactions must be an array');
      if (transactions.some((tx) => !tx.to || !tx.value || !tx.data))
        throw new Error('Transactions must have a to, value and data field');

      return {
        transactions: transactions.map((tx) => ({
          to: tx.to,
          data: tx.data,
          value: BigInt(tx.value),
        })),
        paymaster: isSponsor ? paymaster : undefined,
        gasToken: isSponsor ? gasToken : undefined,
      };
    },
    [getPaymaster, isSponsor],
  );

  /**
   * @param {object} {transactions, paymaster, gasToken, override} - Transaction object
   * @param {object} paymaster - Paymaster object
   * @param {object} gasToken - token to charge gas fee
   * @param {object} override - Override object
   * @param {function} override.onSuccess - On success handler
   * @param {function} override.onError - On error handler
   * @param {function} override.onSubmit - On submit handler
   */
  const getTransactionProvider = useCallback(
    async ({ override }: IGetTransactionProvider) => {
      if (!smartAccountClient?.account)
        throw new Error('Smart Account not found');

      return {
        sendTransactions: async (transactions: TTransaction[]) => {
          try {
            const calls = transactions.map((tx) => {
              return {
                to: tx.to,
                data: tx.data as Hex,
                value: BigInt(tx.value),
              } as Call;
            });

            if (override?.onSubmit) override.onSubmit(calls);
            else handleSubmit(calls);

            const txHash = await smartAccountClient.sendTransaction({
              calls,
            });

            return { txHash };
          } catch (error) {
            console.error('Pimlico: Error sending transaction: ', error);
            return { txHash: undefined };
          }
        },
      };
    },
    [smartAccountClient, handleSubmit],
  );

  /*
   * @param {object} {transactions, paymaster, gasToken, override} - Transaction object
   * @param {array} transactions - Array of transaction objects
   * @param {object} paymaster - Paymaster object
   * @param {object} gasToken - Gas token object
   * @param {object} override - Override object
   * @param {function} override.onSuccess - On success handler
   * @param {function} override.onError - On error handler
   * @param {function} override.onSubmit - On submit handler
   * */

  // TODO-DIEGO: Use this to execute the transaction
  const executeTransactions = useCallback(
    async ({
      transactions,
      paymaster,
      gasToken,
      override,
    }: IExecuteTransaction) => {
      if (!transactions || transactions.length === 0) {
        if (override?.onError) override.onError('No transactions provided');
        else handleError('No transactions provided');
        return;
      }

      const transactionHandler = await getTransactionProvider({
        override,
        paymaster,
        gasToken,
      });

      const { txHash } =
        await transactionHandler.sendTransactions(transactions);

      if (!txHash) {
        if (override?.onError) override.onError('Error sending transaction');
        else handleError('Error sending transaction');
        return;
      }

      if (override?.onSuccess) override.onSuccess(txHash);
      else handleSuccess(txHash);

      return txHash;
    },
    [getTransactionProvider, handleSuccess, handleError],
  );

  return {
    buildTransactions,
    executeTransactions,
  };
};
